<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left" v-if="user.type === 'admin'">Assets</h1>
                <h1 class="text-left" v-else>Your Assets</h1>
                <v-card
                    class="w-50"
                    color="surface-light"
                >
                    <v-card-text class="pa-2">
                        <v-text-field
                            v-model="quickSearch"
                            append-inner-icon="mdi-magnify"
                            autofocus
                            clearable
                            density="compact"
                            hide-details
                            label="Quick Search"
                            single-line
                            variant="solo"
                            @click:clear="fetchAssets"
                            @keydown.enter="applyQuickSearch"
                            @click:append-inner="applyQuickSearch"
                        ></v-text-field>
                    </v-card-text>
                </v-card>
                <v-btn color="black" prepend-icon="mdi-plus" @click="duplicateFrom = null; createDialogSwitch = true">Add</v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col class="d-flex justify-space-between" cols="12">
                <div>
                    <v-chip-group v-if="assetCounts && assetCounts.length">
                        <v-chip v-for="ac in assetCounts" :key="ac.key"
                                :color="ac.count > 0 ? ac.color : undefined"
                                :variant="ac.count > 0 ? 'tonal' : 'outlined'"
                                :prepend-icon="ac.icon"
                                density="comfortable"
                                size="small"
                                class="cursor-pointer"
                                @click="applyCountFilter(ac)">
                            {{ ac.label }}: {{ ac.count }}
                        </v-chip>
                    </v-chip-group>
                </div>
                <div class="d-flex align-center">
                    <v-btn-toggle v-model="showDisabled" density="compact" variant="outlined" color="primary" class="me-2"
                                  @update:model-value="currentPage = 1; fetchAssets()">
                        <v-btn :value="true" size="small">
                            <v-icon start :icon="showDisabled ? 'mdi-eye' : 'mdi-eye-off'"/>{{ showDisabled ? 'Hide Disabled' : 'Show Disabled' }}
                        </v-btn>
                    </v-btn-toggle>
                    <v-btn class="me-2" icon="mdi-refresh" size="small" variant="text"
                           @click="()=>{currentPage=1;fetchAssets()}"/>
                    <v-btn class="me-2" icon="mdi-filter-variant" size="small" variant="text"
                           @click="filtersDialogSwitch = true"/>
                    <v-menu v-if="Object.keys(activeFilters).length">
                        <template v-slot:activator="{ props }">
                            <v-btn icon="mdi-filter-menu" size="small" v-bind="props"
                                   variant="text"/>
                        </template>

                        <v-list class="border pa-0" density="compact">
                            <v-list-item v-for="(filter,key) in activeFilters" :key="key" class="py-0 border-b">
                                <div class="d-flex justify-space-between align-center">
                                    <span>{{ filter.label }}</span>
                                    <v-btn icon="mdi mdi-close" size="small" variant="plain"
                                           @click="removeFilter(key)"/>
                                </div>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th></th>
                        <th-btn title="Name" column="label" :sortedColumn="sortedColumn" sortable @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn" column="last_updated" sortable title="Status"
                                @sort="sort"/>
                        <th-btn v-if="user.type === 'admin'" :sortedColumn="sortedColumn" column="company_name"
                                sortable title="Company"
                                @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn" column="driver_name" sortable title="Driver" @sort="sort"/>
                        <th-btn title="Device" column="slno" :sortedColumn="sortedColumn" sortable @sort="sort"/>
                        <th>Asset Monitoring Configs</th>
                        <th>Asset Groups</th>
                        <th>Known Location</th>
                        <th-btn :sortedColumn="sortedColumn" column="device_statuses.last_odometer" sortable
                                title="Last Odometer"
                                @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn" column="last_updated" sortable title="Last Reported"
                                @sort="sort"/>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="asset in assets" v-if="assets.length" :key="asset.id" class="text-left"
                        :style="{'background-color': getAssetRowColor(asset)}">
                        <td>
                            <div class="d-flex justify-space-between ga-1">
                                <v-avatar v-if="asset.asset_type_photo" :image="asset.asset_type_photo" rounded="lg"
                                          size="24"/>
                                <v-avatar v-if="asset.photo" :image="asset.photo" rounded="lg" size="24"/>
                                <UserAvatar v-if="asset.driver_name" :src="asset.driver_photo"
                                            :name="asset.driver_name" rounded="lg" size="24"/>
                            </div>
                        </td>
                        <td class="text-capitalize" style="white-space: nowrap;">
                            <router-link :to="`assets/${asset.id}`" target="_blank">{{ asset.label }}</router-link>
                        </td>
                        <td>
                            <div class="d-flex align-center ga-1 flex-wrap">
                                <v-tooltip :text="statusTooltip(asset)" location="top">
                                    <template v-slot:activator="{ props }">
                                        <v-chip v-bind="props"
                                                :color="getFleetStatusBadge(asset).color"
                                                :prepend-icon="getFleetStatusBadge(asset).icon"
                                                :variant="getFleetStatusBadge(asset).variant || 'tonal'"
                                                size="x-small"
                                                density="comfortable">
                                            {{ getFleetStatusBadge(asset).label }}
                                        </v-chip>
                                    </template>
                                </v-tooltip>
                                <template v-if="asset.slno && !isHealthy(asset)">
                                    <v-tooltip v-if="isNonReporting(asset)" text="Non-Reporting" location="top">
                                        <template #activator="{ props: tp }">
                                            <v-icon v-bind="tp" icon="mdi-signal-off" color="#d70000" size="x-small"/>
                                        </template>
                                    </v-tooltip>
                                    <template v-else>
                                        <v-icon v-bind="signalIcon(asset.signal_bars ?? 0)" size="x-small"/>
                                        <v-icon v-bind="batteryIcon(asset.battery_bars ?? 0, primaryBatteryType(asset))" size="x-small"/>
                                    </template>
                                    <v-tooltip v-if="asset.indication_lamp_status" text="Check Engine" location="top">
                                        <template #activator="{ props: tp }">
                                            <router-link :to="{path: `assets/${asset.id}`, query: {tab: 4}}" target="_blank">
                                                <v-icon v-bind="tp" icon="mdi-engine" color="orange" size="x-small"/>
                                            </router-link>
                                        </template>
                                    </v-tooltip>
                                </template>
                                <v-tooltip v-if="asset.slno && isHealthy(asset)" text="All OK" location="top">
                                    <template #activator="{ props: tp }">
                                        <v-icon v-bind="tp" icon="mdi-check-circle" color="green" size="x-small"/>
                                    </template>
                                </v-tooltip>
                                <!-- >= 0 so a genuinely empty tank still shows, and <= 100
                                     because the value is a percent. The old > 0 hid the one
                                     reading a fleet manager most needs to see. -->
                                <v-tooltip v-if="asset.fuel_level != null && asset.fuel_level >= 0 && asset.fuel_level <= 100 && asset.asset_type_category === 'vehicle'" :text="`Fuel: ${asset.fuel_level}%`" location="top">
                                    <template #activator="{ props: tp }">
                                        <v-chip v-bind="tp" size="x-small" density="comfortable" variant="tonal"
                                                :color="asset.fuel_level <= 15 ? 'red' : asset.fuel_level <= 30 ? 'orange' : 'teal'"
                                                prepend-icon="mdi-gas-station">
                                            {{ asset.fuel_level }}%
                                        </v-chip>
                                    </template>
                                </v-tooltip>
                            </div>
                        </td>
                        <td v-if="user.type === 'admin'" class="text-capitalize">{{ asset.company_name }}</td>
                        <td class="text-capitalize">{{ asset.driver_name }}</td>
                        <td style="white-space: nowrap;">
                            <router-link v-if="asset.slno" :to="`devices/${asset.slno}`" target="_blank">{{ asset.slno }}</router-link>
                        </td>
                        <td>
                            <v-chip-group>
                                <template v-for="(monitoringConfig,index) in asset.monitoring_configs" :key="index">
                                    <v-chip size="x-small" style="max-width: 160px;">
                                        <span class="me-1 text-truncate">{{ monitoringConfig.name }}</span>
                                        <v-icon icon="mdi-close-circle"
                                                @click="monitoringConfigDetach(asset, monitoringConfig)"/>
                                    </v-chip>
                                </template>
                                <v-btn color="green" density="compact" flat icon="mdi-plus" size="small"
                                       @click="monitoringConfig(asset)"/>
                            </v-chip-group>
                        </td>
                        <td>
                            <v-chip-group>
                                <template v-for="(assetGroup,index) in asset.asset_groups" :key="index">
                                    <v-chip size="x-small" style="max-width: 160px;">
                                        <span class="me-1 text-truncate">{{ assetGroup.name }}</span>
                                        <v-icon v-if="assetGroup.type>1"
                                                icon="mdi-close-circle"
                                                @click="assetGroupDetach(asset, assetGroup)"/>
                                    </v-chip>
                                </template>
                                <v-btn color="green" density="compact" flat icon="mdi-plus" size="small"
                                       @click="editUpdateAssetGroup(asset)"/>
                            </v-chip-group>
                        </td>
                        <td class="text-sm-body-2 cursor-pointer">
                            <v-tooltip :text="getKnownLocation(asset)??''" location="top">
                                <template v-slot:activator="{ props }">
                                    <v-chip v-bind="props" variant="text">
                                        <template v-slot:default>
                                            <v-chip density="compact" variant="text"
                                                    @click="openStreetView(asset)">
                                                {{ getKnownLocation(asset) ?? '' + '...' }}
                                            </v-chip>
                                        </template>
                                        <template v-slot:append>
                                            <v-btn
                                                icon="mdi-content-copy"
                                                class="d-inline ms-2"
                                                density="compact"
                                                variant="text"
                                                size="small"
                                                @click="copyText(asset)"
                                            />
                                        </template>
                                    </v-chip>
                                </template>
                            </v-tooltip>
                        </td>
                        <td>{{ asset.last_odometer ? strOperations().unitifyDistance(asset.last_odometer, 'distance', 'km') : '' }}</td>
                        <td>{{
                                asset.last_updated ? formatDateTime(asset.last_updated) : ''
                            }}
                        </td>
                        <td>
                            <div class="d-flex align-center ga-1">
                                <NoteIcon entity-type="assets" :entity-id="asset.id"
                                          :pinned-note="asset.pinned_note?.note"
                                          :note-count="asset.notes_count ?? 0"/>
                                <v-btn-group density="compact" divided variant="outlined">
                                    <v-btn :disabled="alertsDialogSwitch" class="text-primary" prepend-icon="mdi-bell"
                                           size="x-small" @click="showAssetAlerts(asset)">Alerts
                                    </v-btn>
                                    <v-btn :disabled="editDialogSwitch" class="text-warning" prepend-icon="mdi-pencil"
                                           size="x-small" @click="editAsset(asset)">Edit
                                    </v-btn>
                                    <v-btn :disabled="createDialogSwitch" class="text-info" prepend-icon="mdi-content-copy"
                                           size="x-small" @click="duplicateAsset(asset)">Duplicate
                                    </v-btn>
                                    <v-btn class="text-error" prepend-icon="mdi-delete" size="x-small"
                                           @click="deleteAsset(asset)">Delete
                                    </v-btn>
                                </v-btn-group>
                            </div>
                        </td>
                    </tr>
                    <tr v-else>
                        <td colspan="12">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row justify="center" align="center">
            <v-col>
                <div class="d-flex align-center justify-center">
                    <v-pagination v-if="totalPages > 1"
                        v-model="currentPage"
                        :length="totalPages"
                        class="my-4"
                        density="compact"
                        @update:model-value="fetchAssets"
                    ></v-pagination>
                    <v-select v-model="perPage" :items="[10, 25, 50, 100]" density="compact"
                              variant="outlined" hide-details label="Rows" style="width: 100px"
                              class="ms-4 flex-grow-0"
                              @update:model-value="currentPage = 1; fetchAssets()"/>
                </div>
            </v-col>
        </v-row>
    </v-container>

    <Create-Asset v-if="createDialogSwitch" v-model="createDialogSwitch" :duplicateFrom="duplicateFrom" @addAsset="addAsset"/>
    <Edit-Asset v-if="editDialogSwitch" v-model="editDialogSwitch" :asset="selectedAsset" @updateAsset="updateAsset"/>
    <AssetMonitoringConfig v-if="monitoringConfigDialogSwitch" v-model="monitoringConfigDialogSwitch"
                           :asset="selectedAsset" @updateAsset="updateAsset"/>
    <EditUpdateAssetGroup v-if="assetGroupDialogSwitch" v-model="assetGroupDialogSwitch" :asset="selectedAsset"
                          @updateAsset="updateAsset"/>
    <Asset-Alerts v-if="alertsDialogSwitch" v-model="alertsDialogSwitch" :asset="selectedAsset"/>

    <filters-dialog
        v-if="filtersDialogSwitch"
        v-model="filtersDialogSwitch"
        :filters="filters"

        @resetFilters="clearFilters"
        @applyFilters="()=>{ currentPage=1; fetchAssets() }"
    />
</template>

<script setup>
import {computed, onMounted, onUnmounted, reactive, ref, watch} from "vue";
import {useRoute} from "vue-router";
import {geocodeLatLng, isValidAddress} from "@/composables/useGeocode";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import ThBtn from "@/components/globals/th-btn.vue";
import UserAvatar from "@/components/UserAvatar.vue";
import {useClipboard} from "@/composables/clipboard";
import {useLoadingStore} from "@/stores/loadingStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {strOperations} from "@/composables/globals";
import EditAsset from "@/components/dialogs/assets/Edit.vue";
import CreateAsset from "@/components/dialogs/assets/Create.vue";
import AssetAlerts from "@/components/dialogs/assets/Alerts.vue";
import FiltersDialog from "@/components/cards/devices/FiltersDialog.vue";
import EditUpdateAssetGroup from "@/components/dialogs/assets/EditUpdateAsset.vue";
import {formatDateTime} from "@/composables/useTimezone";
import AssetMonitoringConfig from "@/components/dialogs/assets/AssetMonitoringConfig.vue";
import {powerSourceIcon, primaryBatteryType} from "@/composables/usePowerSourceIcon";
import NoteIcon from "@/components/dialogs/notes/NoteIcon.vue";

const errors = ref([]);
const assets = ref([]);
const {user} = useAuthStore();
const companies = ref([]);
const locations = ref([]);
const totalPages = ref(1);
const currentPage = ref(1);
const perPage = ref(parseInt(localStorage.getItem('assetPerPage')) || 10);
const assetGroups = ref([]);
const sortedType = ref(null);
const assetCounts = ref([]);
const geocodeCache = ref({});
const quickSearch = ref(null);
const sortedColumn = ref(null);
const showDisabled = ref(undefined);
const selectedAsset = ref(null);
const loadingStore = useLoadingStore();
const {copyToClipboard} = useClipboard();
const {showSnackbar} = useSnackbarStore();
const editDialogSwitch = ref(false);
const createDialogSwitch = ref(false);
const duplicateFrom = ref(null);
const alertsDialogSwitch = ref(false);
const filtersDialogSwitch = ref(false);
const assetGroupDialogSwitch = ref(false);
const monitoringConfigDialogSwitch = ref(false);

const NON_REPORTING_HOURS = 30;

const isNonReporting = (asset) => {
    if (!asset.last_updated) return true;
    const hoursAgo = (Date.now() - new Date(asset.last_updated + 'Z')) / (1000 * 60 * 60);
    return hoursAgo > NON_REPORTING_HOURS;
};

const signalIcon = (strength) => {
    switch (strength) {
        case 0: return { icon: 'mdi-signal-off', color: '#dbdbdb' };
        case 1: return { icon: 'mdi-signal-cellular-outline', color: '#d70000' };
        case 2: return { icon: 'mdi-signal-cellular-1', color: '#ff9e01' };
        case 3: return { icon: 'mdi-signal-cellular-2', color: '#849e00' };
        case 4: case 5: return { icon: 'mdi-signal-cellular-3', color: '#099f00' };
        default: return { icon: 'mdi-signal-off', color: '#dbdbdb' };
    }
};

const batteryIcon = (strength, type = null) => {
    return powerSourceIcon(strength, type)
};

const isHealthy = (asset) => {
    if (!asset.slno) return false;
    if (isNonReporting(asset)) return false;
    if ((asset.signal_bars ?? 0) < 2) return false;
    if ((asset.battery_bars ?? 0) < 1) return false;
    if (asset.indication_lamp_status) return false;
    return true;
};

const getAssetRowColor = (asset) => {
    // DTC/MIL - slightly more red than offline
    if (asset.indication_lamp_status) return '#FFE0E0';
    // Offline - very light red
    const status = asset.fleet_status;
    if (status === 'offline') return '#FFF0F0';
    if (!status && !asset.last_updated) return '#FFF0F0';
    if (!status && asset.last_updated) {
        const hoursAgo = (Date.now() - new Date(asset.last_updated + 'Z')) / (1000 * 60 * 60);
        if (hoursAgo > NON_REPORTING_HOURS) return '#FFF0F0';
    }
    return 'transparent';
};

const fleetStatusMap = {
    moving: { label: 'Moving', color: 'green', icon: 'mdi-car-connected' },
    idling: { label: 'Idling', color: 'amber', icon: 'mdi-car-clock' },
    parked: { label: 'Parked', color: 'grey', icon: 'mdi-car-brake-parking' },
    offline: { label: 'Offline', color: 'red', icon: 'mdi-signal-off' },
};

const getParkedBadge = (asset) => {
    const cat = (asset.asset_type_category || '').toLowerCase();
    const label = (cat === 'vehicle' || cat === 'equipment') ? 'Parked' : 'Stationary';
    return { label, color: 'grey', icon: 'mdi-car-brake-parking' };
};

const getFleetStatusBadge = (asset) => {
    if (!asset.slno) return { label: 'No Device', color: undefined, icon: 'mdi-link-off', variant: 'outlined' };
    if (asset.fleet_status && fleetStatusMap[asset.fleet_status]) {
        if (asset.fleet_status === 'parked') return getParkedBadge(asset);
        return fleetStatusMap[asset.fleet_status];
    }
    // Fallback for assets without fleet_status
    if (!asset.last_updated) {
        return fleetStatusMap.offline;
    }
    const hoursAgo = (Date.now() - new Date(asset.last_updated + 'Z')) / (1000 * 60 * 60);
    if (hoursAgo > NON_REPORTING_HOURS) {
        return fleetStatusMap.offline;
    }
    const minsAgo = (Date.now() - new Date(asset.last_updated + 'Z')) / (1000 * 60);
    const speed = Math.max(asset.gps_speed ?? 0, asset.obd_present ? (asset.obd_speed ?? 0) : 0);
    if (asset.motion_state === 1 && minsAgo <= 3 && speed > 2) {
        return fleetStatusMap.moving;
    }
    if (asset.motion_state === 1 && minsAgo <= 3 && speed <= 2) {
        return fleetStatusMap.idling;
    }
    return getParkedBadge(asset);
};

const statusTooltip = (asset) => {
    const parts = [];
    parts.push(`Asset: ${asset.status || '-'}`);
    parts.push(`Device: ${asset.vehicle_status?.name || '-'}`);
    // /assets sends the SKU-selected channel as voltage1, in millivolts.
    if (asset.voltage1) parts.push(`Battery: ${strOperations().formatVoltage(asset.voltage1)}`);
    return parts.join(' | ');
};
const copyText = (asset) => {
    if (asset.formatted_address
        && asset.formatted_address !== ''
        && asset.formatted_address !== 'Address not found') {
        copyToClipboard(asset.formatted_address)
    } else {
        copyToClipboard(`${asset.latitude}, ${asset.longitude}`);
    }
}
const openStreetView = (asset) => {
    if (asset.formatted_address
        && asset.formatted_address !== ''
        && asset.formatted_address !== 'Address not found') {
        window.open(`http://maps.google.com/maps/dir//${asset.formatted_address ?? ''}`, "_blank");
    } else {
        window.open(`http://maps.google.com/maps/dir//${asset.latitude},${asset.longitude}`, "_blank")
    }
}
const status = (status) => {
    switch (status) {
        case -2:
            return 'Total';
        case -1:
            return 'Non Reporting';
        case 0:
            return 'Active';
        case 1:
            return 'Disabled';
        case 2:
            return 'Faulty';
        case 3:
            return 'Lost';
    }
};
const deviceStatuses = ref([
    {id: 0, title: 'Active'},
    {id: 1, title: 'Disabled'},
    {id: 2, title: 'Faulty'},
    {id: 3, title: 'Lost'}
]);
const filters = reactive({
    label: {label: 'Asset Name', data: null},
    serial_num: {label: 'Serial Number', data: null, action: null},
    asset_group: {
        label: 'Asset Group',
        type: 'select',
        data: null,
        title: 'name',
        value: 'id',
        multiple: true,
        source: assetGroups
    },
    device_status: {
        label: 'Device Status',
        type: 'select',
        data: null,
        title: 'title',
        value: 'id',
        source: deviceStatuses
    },
    asset_status: {
        label: 'Asset Status',
        type: 'select',
        data: null,
        source: [
            {value: 'enabled', title: 'Enabled'},
            {value: 'disabled', title: 'Disabled'},
        ]
    },
    geo_radius: {label: 'Radius', type: 'number', data: null, latitude: null, longitude: null},
    imei: {label: 'IMEI', data: null, action: null},
    last_update: {label: 'Last Updated', data: null, action: null, type: 'datetime-local'},
});
if (user.type === 'admin') {
    filters['company'] = {
        label: 'Company',
        // Autocomplete, not select: a plain select shows bare company names, and a
        // Go account's name is just its owner's, so several read identically.
        type: 'autocomplete',
        data: null,
        title: 'company_name',
        value: 'id',
        searchKeys: ['company_name', 'owner_name', 'email', 'account_code', 'id'],
        source: companies
    };
}
const activeFilters = computed(() => {
    const activatedFilters = {};
    for (const [key, filter] of Object.entries(filters)) {
        if (filter.data !== null) {
            quickSearch.value = null;
            activatedFilters[key] = filter;
        }
    }
    return activatedFilters;
});
const removeFilter = (key) => {
    filters[key]['data'] = null;
    filters[key]['action'] ? filters[key]['action'] = null : '';
    totalPages.value = 1;
    currentPage.value = 1;
    assetCounts.value = [];
    sortedType.value = null;
    fetchAssets();
}
const clearFilters = () => {
    Object.keys(filters).forEach((key) => {
        ['data', 'action', 'latitude', 'longitude'].forEach(field => {
            if (filters[key] && filters[key].hasOwnProperty(field)) {
                filters[key][field] = null;
            }
        });
    });
    totalPages.value = 1;
    currentPage.value = 1;
    assetCounts.value = [];
    sortedType.value = null;
    fetchAssets();
}
const applyCountFilter = (ac) => {
    // Clear existing filters first
    Object.keys(filters).forEach((key) => {
        ['data', 'action', 'latitude', 'longitude'].forEach(field => {
            if (filters[key] && filters[key].hasOwnProperty(field)) {
                filters[key][field] = null;
            }
        });
    });
    // 'total' clears all filters; others set fleet_status
    if (ac.key !== 'total') {
        filters['fleet_status'] = {label: ac.label, data: ac.key};
    }
    currentPage.value = 1;
    fetchAssets();
};
const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    fetchAssets();
}
const getKnownLocation = (asset) => {
    if (asset.location) return asset.location;
    // Skip cached values that are the old "Address not found" sentinel - older
    // builds persisted those on geocode failure, and we want to re-attempt
    // rather than display the bad string forever.
    if (isValidAddress(asset.formatted_address)) return asset.formatted_address;

    const key = asset.id;

    if (geocodeCache.value[key]) return geocodeCache.value[key];

    // No address cached: show the raw coords while the async geocode runs.
    if (!asset.latitude || !asset.longitude) {
        return '-';
    }
    geocodeCache.value[key] = `${asset.latitude}, ${asset.longitude}`;

    geocodeLatLng({lat: asset.latitude, lon: asset.longitude})
        .then((response) => {
            // Composable now returns null on every failure path; only persist
            // and display real addresses.
            if (!isValidAddress(response)) return;
            geocodeCache.value[key] = response;
            const index = assets.value.findIndex((item) => item.id === asset.id);
            if (index !== -1) {
                assets.value[index]['formatted_address'] = response;
                updateGeocodedAddress(asset.device_id, response);
            }
        })
        .catch(() => { /* leave the coord placeholder, retry on next load */ });

    return geocodeCache.value[key];
};
const applyQuickSearch = () => {
    if (quickSearch.value) {
        clearFilters();
    }
}

const assetRoute = useRoute();
onMounted(async () => {
    const initialFleetStatus = assetRoute.query.fleet_status;
    if (initialFleetStatus) {
        filters['fleet_status'] = {label: initialFleetStatus.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), data: initialFleetStatus};
    }
    await fetchAssets()
    await fetchCompaniesList()
    await fetchAssetGroupsList()
});

// Opened again from the menu count while already on this page.
watch(() => assetRoute.query.fleet_status, (status) => {
    if (assetRoute.name !== 'assets') return;
    // The plain menu item has no fleet_status: clear a stale one.
    applyCountFilter(status
        ? {key: status, label: status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
        : {key: 'total'});
});

onUnmounted(() => {
    const localStorageProfiles = JSON.parse(localStorage.getItem('filterProfiles'));
    if (localStorageProfiles?.selectedProfile) {
        localStorage.setItem('filterProfiles', JSON.stringify({
            selectedProfile: null,
            profiles: localStorageProfiles.profiles,
        }));
    }
});

const fetchAssetPhotos = (assetId) => {
    send('get', `assets/${assetId}/photo-url`)
        .then((response) => {
            if (response.data.success && response.data.data.url) {
                const assetIndex = assets.value.findIndex((asset) => asset.id === assetId)
                if (assetIndex > -1) {
                    assets.value[assetIndex]['photo'] = response.data.data.url;
                }
            }
        })
        .catch((error) => {
            console.log(error)
        })
}
const fetchAssets = () => {
    localStorage.setItem('assetPerPage', perPage.value);
    let data = {
        page: currentPage.value,
        per_page: perPage.value,
        order: sortedType.value,
        sort: sortedColumn.value,
        quick_search: quickSearch.value,
    };
    if (showDisabled.value) data.include_disabled = 1;
    loadingStore.showLoading('Loading...', 'Fetching your Assets');
    if (Object.keys(activeFilters.value).length) {
        send('post', `assets/filtered`, {...data, ...filters})
            .then((response) => {
                assets.value = response.data.data.assets.data;
                totalPages.value = response.data.data.assets.last_page;
                currentPage.value = response.data.data.assets.current_page;
                assetCounts.value = response.data.data.asset_counts;
            })
            .catch((error) => {
                console.log(error)
                showSnackbar('Assets could not be loaded', 'error')
            })
            .finally(() => loadingStore.hideLoading());
    } else {
        send('get', `assets`, data)
            .then((response) => {
                assets.value = response.data.data.assets.data;
                totalPages.value = response.data.data.assets.last_page;
                currentPage.value = response.data.data.assets.current_page;
                assetCounts.value = response.data.data.asset_counts;
            })
            .catch((error) => {
                console.log(error)
                showSnackbar('Assets could not be loaded', 'error')
            })
            .finally(async () => {
                loadingStore.hideLoading();
                if (user && user.type === 'company' && assets.value.length) {
                    if (!locations.value.length) {
                        await fetchCompanyLocations();
                    }
                    if (locations.value.length) {
                        checkDevicesLocation();
                    }
                }
            });
    }
}
const fetchCompaniesList = async () => {
    await send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        }).catch((error) => {
            showSnackbar('Companies List could not be loaded', 'error')
        })
}
const fetchAssetGroupsList = async () => {
    await send('get', `asset-groups?list=true`)
        .then((response) => {
            assetGroups.value = response.data.data;
        }).catch((error) => {
            console.log(error)
            showSnackbar('Companies could not be loaded', 'error')
        })
}
const fetchCompanyLocations = async () => {
    if (user && user.type === 'company') {
        await send("get", `companies/locations/${user.company.id}`, {list: true})
            .then((response) => {
                locations.value = response.data.data;
            }).catch((errorResponse) => console.log(errorResponse));
    }
}

const updateGeocodedAddress = (device, address) => {
    send("put", `devices/update-address`, {device: device, address: address})
        .catch((errorResponse) => console.log(errorResponse));
}
const isPointInPolygon = (polygon, point) => {
    let inside = false;
    const x = parseFloat(point['longitude']);  // X = lon
    const y = parseFloat(point['latitude']);   // Y = lat

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = parseFloat(polygon[i]['longitude']);
        const yi = parseFloat(polygon[i]['latitude']);
        const xj = parseFloat(polygon[j]['longitude']);
        const yj = parseFloat(polygon[j]['latitude']);

        const intersect =
            ((yi > y) !== (yj > y)) &&
            (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

        if (intersect) {
            inside = !inside;
        }
    }

    return inside;
}
const checkDevicesLocation = () => {
    setTimeout(function () {
        const assetsWithUnknownLocation = assets.value.filter((item, index) => {
            item.index = index;
            return item.location_updated && !item.location;
        });

        if (assetsWithUnknownLocation.length) {
            assetsWithUnknownLocation.map((item) => {
                locations.value.filter((location) => {
                    if (isPointInPolygon(location.polygon_coordinates, {
                        latitude: item.latitude,
                        longitude: item.longitude
                    })) {
                        setTimeout(updateDeviceLocation(item, location), 1000)
                    }
                })
                return item.location_id && !item.location;
            });
        }
    }, 3000)
}
const updateDeviceLocation = (asset, location = null) => {
    if (asset.index < 0) {
        return;
    }
    assets.value[asset.index]['location'] = location.name;
    send("put", `devices/location`, {
        device: asset.device_id,
        location: location.id
    }).catch((errorResponse) => console.log(errorResponse));
}

const addAsset = (asset) => {
    // assets.value.unshift(asset);
    currentPage.value = 1;
    fetchAssets();
}
const duplicateAsset = (asset) => {
    duplicateFrom.value = asset;
    createDialogSwitch.value = true;
}
const editAsset = (asset) => {
    selectedAsset.value = asset;
    editDialogSwitch.value = true;
}

const monitoringConfig = (asset) => {
    selectedAsset.value = asset;
    monitoringConfigDialogSwitch.value = true;
}
const monitoringConfigDetach = (asset, monitoring_config) => {
    errors.value = [];
    if (confirm(`Are you sure to detach "${monitoring_config.name}" from "${asset.label}" asset?`)) {
        const monitoring_config_ids = [];
        asset.monitoring_configs.map((config) => {
            if (config.id !== monitoring_config.id) {
                monitoring_config_ids.push(config.id);
            }
        });
        send('post', `assets/${asset.id}/monitoring-configs`, {monitoring_configs: monitoring_config_ids})
            .then((response) => {
                showSnackbar(response.data.message);
                if (response.data.success) {
                    updateAsset(response.data.data);
                }
            })
            .catch(function (errorResponse) {
                if (errorResponse.response.data.errors) {
                    let validationErrors = errorResponse.response.data.errors;
                    Object.keys(validationErrors).forEach((key) => {
                        errors.value.push(validationErrors[key][0]);
                    });
                } else if (errorResponse.response.data.message) {
                    errors.value.push(errorResponse.response.data.message);
                }
                showSnackbar('Something went wrong', 'error');
            })
    }
}
const editUpdateAssetGroup = (asset) => {
    selectedAsset.value = asset;
    assetGroupDialogSwitch.value = true;
}
const assetGroupDetach = (asset, asset_group) => {
    errors.value = [];

    if (confirm(`Are you sure to detach "${asset_group.name}" from "${asset.label}" asset?`)) {
        send('delete', `assets/${asset.id}/asset-group/${asset_group.id}`)
            .then((response) => {
                showSnackbar(response.data.message);
                if (response.data.success) {
                    fetchAssets()
                }
            })
            .catch(function (errorResponse) {
                showSnackbar(errorResponse.response.data.message ?? 'Something went wrong', 'error');
            })
    }
}

const showAssetAlerts = (asset) => {
    selectedAsset.value = asset;
    alertsDialogSwitch.value = true;
}
const updateAsset = () => {
    fetchAssets();
}
const deleteAsset = (asset) => {
    if (confirm(`Are you sure to delete "${asset.label}" asset?`)) {
        send('delete', `assets/${asset.id}`)
            .then((response) => {
                if (response.data.success) {
                    assets.value.map((item, index) => {
                        if (item.id === asset.id) {
                            assets.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${asset.label}" Asset could not be deleted`, 'error')
                }
            })
    }
}
</script>
